import React from 'react';
import { Clock, Layers } from 'lucide-react';
import { MusicIcon, VisualArtIcon, WritingIcon, VideoIcon } from '../icons';
import { ProjectBoardDto, ProjectCardDto } from '../../services/projects';
import './ProjectGrid.css';

interface ProjectGridProps {
  board: ProjectBoardDto;
  onProjectClick?: (project: ProjectCardDto) => void;
}

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'Music':
      return <MusicIcon size={20} />;
    case 'Art':
      return <VisualArtIcon size={20} />;
    case 'Writing':
      return <WritingIcon size={20} />;
    case 'Video':
      return <VideoIcon size={20} />;
    default:
      return <Layers size={20} />;
  }
};

const getStageColor = (stageName: string): string => {
  const stageColors: Record<string, string> = {
    // Universal stages
    'Planning': '#54A0FF',
    'In Progress': '#A78BFA',
    'Review': '#FF6B9D',
    'Complete': '#5CD85A',
    // Music stages
    'Idea': '#54A0FF',
    'Composition': '#A78BFA',
    'Recording': '#FDBA74',
    'Mixing': '#86EFAC',
    'Mastering': '#FDE047',
    // Art stages
    'Concept': '#54A0FF',
    'Sketch': '#A78BFA',
    'Draft': '#FDBA74',
    'Refinement': '#86EFAC',
    'Final': '#FDE047',
    // Writing stages
    'Outlining': '#A78BFA',
    'Drafting': '#FDBA74',
    'Editing': '#86EFAC',
    'Publishing': '#FDE047',
    // Video stages
    'Pre-Production': '#54A0FF',
    'Filming': '#A78BFA',
    'Post-Production': '#FDBA74',
    'Color Grading': '#86EFAC',
    'Export': '#FDE047',
  };
  return stageColors[stageName] || '#A78BFA';
};

const formatRelativeTime = (dateStr: string | null): string => {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return `${Math.floor(diffDays / 30)} months ago`;
};

export const ProjectGrid: React.FC<ProjectGridProps> = ({
  board,
  onProjectClick,
}) => {
  // Flatten all projects from all columns
  const allProjects = board.columns.flatMap(column =>
    column.projects.map(project => ({
      ...project,
      columnName: column.stageName,
    }))
  );

  if (allProjects.length === 0) {
    return null;
  }

  return (
    <div className="project-grid">
      <div className="project-grid__container">
        {allProjects.map((project) => (
          <button
            key={project.id}
            className="project-grid__card"
            onClick={() => onProjectClick?.(project)}
          >
            {/* Accent bar */}
            <div
              className="project-grid__accent"
              style={{ backgroundColor: getStageColor(project.columnName) }}
            />

            {/* Card content */}
            <div className="project-grid__content">
              {/* Category icon */}
              <div className="project-grid__icon">
                {getCategoryIcon(project.projectType)}
              </div>

              {/* Project info */}
              <div className="project-grid__info">
                <h3 className="project-grid__title">{project.title}</h3>
                {project.description && (
                  <p className="project-grid__description">{project.description}</p>
                )}
              </div>

              {/* Stage badge */}
              <span
                className="project-grid__stage"
                style={{
                  backgroundColor: `${getStageColor(project.columnName)}20`,
                  color: getStageColor(project.columnName),
                }}
              >
                {project.currentStage || project.columnName}
              </span>

              {/* Footer */}
              <div className="project-grid__footer">
                <span className="project-grid__time">
                  <Clock size={12} />
                  {formatRelativeTime(project.lastWorkedDate || null)}
                </span>
                {project.isParentProject && (
                  <span className="project-grid__opus-badge">OPUS</span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ProjectGrid;
